/* eslint-disable jsx-a11y/alt-text */
import {
    Box,
    Breadcrumbs,
    Button,
    Chip,
    Divider,
    IconButton,
    Paper,
    Tooltip,
    Typography
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Grid from "@mui/material/Grid2";
import { DataGrid, GridColDef, GridPinnedColumnFields } from "@mui/x-data-grid";
import { getOrgProfileApi } from "../Services/OrgRepo";
import { showToast } from "../../Components/Toast";
import { ThreeDot } from "react-loading-indicators";
import {
    accountManager,
    accountManagerHead,
    NPDManagerHead,
    productionManagerHead,
    qualityManagerHead,
    sourcingManagerHead,
    getProfilePic,
    generateCompanyLogo,
    generateOrganisationLogo,
    Management,
    formatCurrencyValue
} from "../../Utils/Constant";
import { DataGridPro } from "@mui/x-data-grid-pro";
import defaultlogo from "../../assets/icons/noimg.png";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import EuroIcon from "@mui/icons-material/Euro";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const getCurrencyIcon = (currency) => {
    switch (currency) {
        case "INR":
            return <CurrencyRupeeIcon sx={{ fontSize: "0.875rem" }} />;
        case "USD":
            return <AttachMoneyIcon sx={{ fontSize: "0.875rem" }} />;
        case "EUR":
            return <EuroIcon sx={{ fontSize: "0.875rem" }} />;
        default:
            return null;
    }
};
const columns: GridColDef<AssociatedCompany>[] = [
    {
        field: "companyID",
        headerName: "Company Id",
        width: 150,
        disableColumnMenu: true
    },
    {
        field: "companyName",
        headerName: "Company Name",
        width: 370,
        disableColumnMenu: true,
        renderCell: (params) => {
            const logoUrl = params.row.companyImg
                ? generateCompanyLogo(
                      params.row.companyRecID,
                      params.row.companyImg
                  )
                : defaultlogo;
            return (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        height: "100%",
                        cursor: "pointer"
                    }}
                >
                    {logoUrl && (
                        <img
                            src={logoUrl}
                            alt={""}
                            width={24}
                            height={24}
                            style={{ borderRadius: "50%" }}
                        />
                    )}
                    <Typography variant="body2" noWrap>
                        {params.row.companyName}
                    </Typography>
                </Box>
            );
        }
    }
    // {
    //   field: "status",
    //   headerName: "Status",
    //   width: 180,
    //   disableColumnMenu: true,
    //   renderCell: (params) => (
    //     <Chip
    //       label={params.value === "Active" ? "Active" : "Inactive"}
    //       color={params.value === "Active" ? "success" : "default"}
    //       variant="outlined"
    //       size="small"
    //     />
    //   ),
    // },
];

interface OrganisationProfile {
    nameOftheOrganisation: string;
    orgID: string;
    status: string;
    lastUpdated: string;
    orgImg: string;
}

interface About {
    keyAccountManagerID: string;
    keyAccountManager: string;
    organisationTurnover: string;
    locationType: string;
    about: string;
    origin: string;
    isThisAnIndianSME: string;
}

interface AssociatedCompany {
    companyID: string;
    companyName: string;
    status: string;
    companyImg: string;
    companyRecID: string;
}

interface AssociatedCompanyRFQ {
    rfqID: string;
    associatedCompany: string;
    rfqName: string;
    rfqStartDate: string;
    rfqEndDate: string;
    totalOrder: string;
    totalCoPoValue: number;
    status: string;
    currency: string;
    priceType: string;
}

interface OrganisationData {
    Organisation_Profile: OrganisationProfile;
    About: About;
    Associated_Companies: AssociatedCompany[];
    Associated_Company_RFQ: AssociatedCompanyRFQ[];
}

export const OrgProfilePage: React.FC = () => {
    const location = useLocation();
    // const { orgId } = location.state || {};
    const storedData = JSON.parse(
        localStorage.getItem("OrgProfilePage") || "null"
    );
    const orgId = location.state?.orgId || storedData?.orgId;

    const [orgProfileDetails, setOrgProfileDetails] =
        useState<OrganisationData | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [pinnedColumns, setPinnedColumns] =
        React.useState<GridPinnedColumnFields>({
            left: ["rfqID", "rfqName"],
            right: ["status"]
        });
    const handlePinnedColumnsChange = React.useCallback(
        (updatedPinnedColumns: GridPinnedColumnFields) => {
            setPinnedColumns(updatedPinnedColumns);
        },
        []
    );
    const [totalOrderInINR, setTotalOrderInINR] = useState(0);
    const [totalCoPoInINR, setTotalCoPoInINR] = useState(0);

    const fetchOrgProfileDetails = async () => {
        setIsLoading(true);
        try {
            const response = await getOrgProfileApi(orgId);
            setIsLoading(false);

            if (response?.status && response?.data?.length > 0) {
                const orgData = response.data[0];
                setOrgProfileDetails(orgData);

                // ✅ Extract RFQ data
                const rfqs = orgData?.Associated_Company_RFQ || [];

                // ✅ Conversion rates (update as needed)
                const conversionRates = {
                    USD: 88,
                    EUR: 103,
                    INR: 1
                };

                // ✅ Calculate total in INR
                const totalInINR = rfqs.reduce((sum, rfq) => {
                    const currency = rfq.currency || "INR";
                    const rate = conversionRates[currency] || 1;
                    const totalOrder = parseFloat(rfq.totalOrder) || 0;
                    return sum + totalOrder * rate;
                }, 0);

                const totalCoPoInINR = rfqs.reduce((sum, rfq) => {
                    const currency = rfq.currency || "INR";
                    const rate = conversionRates[currency] || 1;
                    const totalOrder = parseFloat(rfq.totalCoPoValue) || 0;
                    return sum + totalOrder * rate;
                }, 0);

                console.log("✅ Total Order Value (in INR):", totalInINR);

                // optional: store in state for UI display
                setTotalOrderInINR(totalInINR);
                setTotalCoPoInINR(totalCoPoInINR);
            } else {
                setOrgProfileDetails(null);
            }
        } catch (error) {
            setIsLoading(false);
            setOrgProfileDetails(null);
            showToast({
                message: error?.message,
                type: "error"
            });
        }
    };

    useEffect(() => {
        fetchOrgProfileDetails();
    }, [orgId]);

    const isHighValueRfq = (row) => {
        const { currency, totalOrder } = row;

        return (
            (currency === "INR" && totalOrder >= 5000000) ||
            (currency === "EUR" && totalOrder >= 50000) ||
            (currency === "USD" && totalOrder >= 50000)
        );
    };

    const columnsRfq: GridColDef<AssociatedCompanyRFQ>[] = [
        {
            field: "rfqID",
            headerName: "CORFQ ID",
            width: 120,
            disableColumnMenu: true
        },
        {
            field: "rfqName",
            headerName: "CORFQ Name",
            width: 200,
            disableColumnMenu: true,
            cellClassName: (params) => {
                return isHighValueRfq(params?.row) ? "rfq-high-value-cell" : "";
            },
            renderCell: (params) => {
                const highValue = isHighValueRfq(params?.row);

                return (
                    <>
                        {highValue && (
                            <>
                                <div className="highlightChip">
                                    High value{" "}
                                    <TrendingUpIcon sx={{ fontSize: "1rem" }} />
                                </div>
                            </>
                        )}
                        <Link
                            to="#"
                            style={{
                                textDecoration: "none",
                                display: "flex",
                                justifyContent: "space-between",
                                width: "100%",
                                alignItems: "center"
                            }}
                        >
                            <span>{params?.value}</span>
                        </Link>
                    </>
                );
            }
        },
        // {
        //   field: "rfqName",
        //   headerName: "CORFQ Name",
        //   width: 150,
        //   disableColumnMenu: true,
        // },
        {
            field: "associatedCompany",
            headerName: "Associated Company",
            width: 250,
            disableColumnMenu: true,

            renderCell: (params) => {
                const companyData =
                    orgProfileDetails?.Associated_Companies?.filter(
                        (item) =>
                            item.companyName === params.row.associatedCompany
                    );
                const logoUrl = companyData[0].companyImg
                    ? generateCompanyLogo(
                          companyData[0]?.companyRecID,
                          companyData[0].companyImg
                      )
                    : defaultlogo;
                return (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            height: "100%"
                        }}
                    >
                        {logoUrl && (
                            <img
                                src={logoUrl}
                                alt={""}
                                width={28}
                                height={28}
                                style={{
                                    borderRadius: "50%",
                                    border: "1px solid #D8D8D8"
                                }}
                            />
                        )}
                        <Typography variant="body2">{params.value}</Typography>
                    </Box>
                );
            }
        },
        {
            field: "rfqStartDate",
            headerName: "CORFQ Start Date",
            width: 250,
            disableColumnMenu: true
        },
        {
            field: "rfqEndDate",
            headerName: "CORFQ End Date",
            width: 150,
            disableColumnMenu: true
        },
        {
            field: "totalOrder",
            headerName: "Total Order Value",
            width: 150,
            disableColumnMenu: true,
            renderCell: (params) => {
                const value = params.value;
                const currency = params?.row?.currency;
                const formattedValue = formatCurrencyValue(value, currency);
                return (
                    <>
                        <div style={{ display: "block" }}>
                            <Typography
                                variant="body2"
                                display="flex"
                                alignItems="center"
                            >
                                {getCurrencyIcon(params?.row?.currency)}
                                <span style={{ paddingLeft: "5px" }}>
                                    {formattedValue}
                                </span>
                                <Tooltip title={params?.row?.priceType}>
                                    <IconButton>
                                        <InfoOutlinedIcon
                                            style={{ fontSize: "16px" }}
                                        />
                                    </IconButton>
                                </Tooltip>
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                            >
                                {params?.row?.currency}
                            </Typography>
                        </div>
                    </>
                );
            }
        },
        {
            field: "department",
            headerName: "Department",
            width: 180,
            disableColumnMenu: true,
            renderCell: (params) => {
                return (
                    <Chip
                        label={params.value}
                        color="default"
                        variant="outlined"
                        size="small"
                    />
                );
            }
        },
        {
            field: "status",
            headerName: "CORFQ Status",
            width: 200,
            disableColumnMenu: true,
            cellClassName: () => "first-pinned-right",
            headerClassName: "first-pinned-right",
            renderCell: (params) => {
                const status = params?.row?.status || "-"; // Show '-' if status is missing
                let color:
                    | "default"
                    | "info"
                    | "success"
                    | "error"
                    | "warning"
                    | "primary"
                    | "secondary" = "warning"; // Default color
                if (status !== "-") {
                    if (
                        sourcingManagerHead() ||
                        qualityManagerHead() ||
                        NPDManagerHead() ||
                        productionManagerHead()
                    ) {
                        switch (status) {
                            case "Approved":
                                color = "success";
                                break;
                            case "Rejected":
                                color = "error";
                                break;
                            case "Partially Approved":
                                color = "warning";
                                break;
                            default:
                                color = "default";
                        }
                    } else if (
                        accountManagerHead() ||
                        accountManager() ||
                        Management()
                    ) {
                        switch (status) {
                            case "Approved":
                                color = "success";
                                break;
                            case "Rejected":
                                color = "error";
                                break;
                            case "Approvers Review Pending":
                                color = "default";
                                break;
                            case "KAM Head Review Pending":
                                color = "warning";
                                break;
                            default:
                                color = "default";
                        }
                    }
                }
                return status === "-" ? (
                    "-"
                ) : (
                    <Chip
                        label={
                            status === "Approved"
                                ? "Approved by KAM Head"
                                : status === "Rejected"
                                  ? "Rejected by KAM Head"
                                  : status
                        }
                        color={color}
                        variant="outlined"
                        size="small"
                        sx={
                            status === "Approvers Review Pending"
                                ? {
                                      borderColor: "#FFB400",
                                      color: "#FFB400",
                                      backgroundColor: " #f8b71417!important"
                                  }
                                : {}
                        }
                    />
                );
            }
        }
    ];

    return isLoading ? (
        <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="50vh"
        >
            <ThreeDot color="#1976d2" size="medium" text="" textColor="" />
        </Box>
    ) : orgProfileDetails ? (
        <>
            <Breadcrumbs aria-label="breadcrumb" sx={{ marginBottom: "10px" }}>
                <Typography key="account" sx={{ fontSize: "14px" }}>
                    <Link
                        to="/layout/Dashboard"
                        style={{
                            color: "#0070BA",
                            fontSize: "14px",
                            textDecoration: "none"
                        }}
                    >
                        Home
                    </Link>
                </Typography>
                <Typography sx={{ fontSize: "14px" }}>
                    <Link
                        to="/layout/OrgRepo"
                        style={{ color: "#0070ba", textDecoration: "none" }}
                    >
                        Organisation Repository
                    </Link>
                </Typography>
                <Typography sx={{ fontSize: "14px" }}>
                    {orgProfileDetails?.Organisation_Profile
                        ?.nameOftheOrganisation || "-"}
                </Typography>
            </Breadcrumbs>
            <Box
                mb={2}
                sx={{ display: "flex", justifyContent: "space-between" }}
            >
                <div style={{ display: "flex" }}>
                    {orgProfileDetails?.Organisation_Profile?.orgImg && (
                        <div>
                            <img
                                src={generateOrganisationLogo(
                                    orgId,
                                    orgProfileDetails?.Organisation_Profile
                                        ?.orgImg
                                )}
                                width={60}
                                height={60}
                                style={{
                                    marginRight: "5px",
                                    borderRadius: "50%"
                                }}
                            />
                        </div>
                    )}
                    <div style={{ marginLeft: "10px" }}>
                        <Typography variant="h5" mb={1}>
                            {orgProfileDetails?.Organisation_Profile
                                ?.nameOftheOrganisation || "-"}
                        </Typography>
                        <Box
                            sx={{
                                display: "inline-flex"
                            }}
                        >
                            <Typography variant="body2" color="text.secondary">
                                {orgProfileDetails?.Organisation_Profile
                                    ?.orgID || "-"}
                            </Typography>
                            <Divider
                                orientation="vertical"
                                sx={{ margin: "0 10px" }}
                                variant="middle"
                                flexItem
                            />
                            {/* {orgProfileDetails?.Organisation_Profile?.status === "Active" ? (
                <Chip
                  label={"Active"}
                  color="success"
                  variant="outlined"
                  size="small"
                  sx={{ padding: "0 4px", marginRight: "5px" }}
                />
              ) : (
                <Chip
                  label={"InActive"}
                  color="default"
                  variant="outlined"
                  size="small"
                  sx={{
                    padding: "0 4px",
                    marginRight: "5px",
                    border: "1px solid #ccc",
                    color: " #797979",
                  }}
                />
              )} 

              <Divider
                orientation="vertical"
                sx={{ margin: "0 10px" }}
                variant="middle"
                flexItem
              />*/}
                            <Typography variant="body2" color="text.secondary">
                                {orgProfileDetails?.Organisation_Profile
                                    ?.lastUpdated || "-"}
                            </Typography>
                        </Box>
                    </div>
                </div>
                {/* <div>
          {accountManagerHead() && (
            <Button variant="outlined" color="error">
              Deactivate
            </Button>
          )}
        </div> */}
            </Box>

            <Grid container spacing={2}>
                <Grid size={5}>
                    <Paper
                        elevation={0}
                        style={{ padding: "15px 15px", minHeight: "330px" }}
                    >
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 12 }}>
                                <Typography variant="h6" fontWeight="bold">
                                    About
                                </Typography>
                            </Grid>
                            {/* <Grid size={{ xs: 6, md: 6 }}>
                <Typography variant="body2" color="text.secondary" mb={1}>
                  Key Account Manager
                </Typography>
              </Grid> */}
                            {/* <Grid size={{ xs: 6, md: 6 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 2, md: 2 }}>
                    <img
                      src=""
                      alt="KAM"
                      width={30}
                      height={30}
                      style={{ borderRadius: "50%" }}
                      ref={(img) => {
                        if (
                          img &&
                          orgProfileDetails?.About?.keyAccountManagerID
                        ) {
                          getProfilePic(
                            orgProfileDetails.About.keyAccountManagerID
                          ).then((url) => {
                            img.src = url || "-";
                          });
                        }
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 10, md: 10 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        paddingLeft: "5px",
                      }}
                    >
                      {orgProfileDetails?.About?.keyAccountManager || "-"}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid> */}
                            <Grid size={{ xs: 6, md: 6 }}>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    mb={1}
                                >
                                    Description
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 6, md: 6 }}>
                                <Typography
                                    variant="body2"
                                    display="flex"
                                    alignItems="center"
                                >
                                    {orgProfileDetails?.About?.about}
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 6, md: 6 }} mb={1}>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Organisation Turnover
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 6, md: 6 }}>
                                <Typography
                                    variant="body2"
                                    display="flex"
                                    alignItems="center"
                                >
                                    {/* <img
                  src={InstaIcon}
                  width={20}
                  alt="Instagram Icon"
                  style={{ marginRight: 8 }}
                /> */}
                                    {orgProfileDetails?.About
                                        ?.organisationTurnover || "-"}
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 6, md: 6 }}>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    mb={1}
                                >
                                    Total CORFQ Value (in INR)
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 6, md: 6 }}>
                                <Typography
                                    variant="body2"
                                    display="flex"
                                    alignItems="center"
                                >
                                    ₹ {formatCurrencyValue(totalOrderInINR)}
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 6, md: 6 }}>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    mb={1}
                                >
                                    Total COPO Value (in INR)
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 6, md: 6 }}>
                                <Typography
                                    variant="body2"
                                    display="flex"
                                    alignItems="center"
                                >
                                    ₹ {formatCurrencyValue(totalCoPoInINR)}
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 6, md: 6 }}>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    mb={1}
                                >
                                    Presence
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 6, md: 6 }}>
                                <Chip
                                    label={
                                        orgProfileDetails?.About?.locationType
                                    }
                                    color={
                                        orgProfileDetails?.About
                                            ?.locationType === "Domestic"
                                            ? "primary"
                                            : "secondary"
                                    }
                                    variant="outlined"
                                    size="small"
                                />
                            </Grid>
                            <Grid size={{ xs: 6, md: 6 }}>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    mb={1}
                                >
                                    Origin
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 6, md: 6 }}>
                                <Typography
                                    variant="body2"
                                    display="flex"
                                    alignItems="center"
                                >
                                    {orgProfileDetails?.About?.origin}
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 6, md: 6 }}>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    mb={1}
                                >
                                    Is this indian SME ?
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 6, md: 6 }}>
                                <Typography
                                    variant="body2"
                                    display="flex"
                                    alignItems="center"
                                >
                                    {
                                        orgProfileDetails?.About
                                            ?.isThisAnIndianSME
                                    }
                                </Typography>
                            </Grid>

                            {/* <Typography variant="body2">
                  {orgProfileDetails?.About?.locationType || "-"}
                </Typography> */}
                        </Grid>
                    </Paper>
                </Grid>
                <Grid size={7}>
                    <Paper
                        elevation={0}
                        style={{ padding: "10px", minHeight: "330px" }}
                    >
                        <Typography variant="h6" fontWeight="bold" mb={1}>
                            Associated Companies
                        </Typography>
                        {orgProfileDetails?.Associated_Company_RFQ?.length >
                        0 ? (
                            <DataGridPro
                                rows={
                                    orgProfileDetails?.Associated_Companies ||
                                    []
                                }
                                columns={columns}
                                initialState={{
                                    pagination: {
                                        paginationModel: { pageSize: 3 }
                                    }
                                }}
                                getRowId={(row) => row.companyID}
                                disableRowSelectionOnClick
                                sx={{
                                    "& .MuiDataGrid-columnHeaders": {
                                        position: "sticky",
                                        top: 0,
                                        zIndex: 1000,
                                        background: "#E3E8EF"
                                    }
                                }}
                            />
                        ) : (
                            <Typography
                                variant="h6"
                                textAlign="center"
                                mt={2}
                                color="text.secondary"
                            >
                                CORFQs associated with this organisation will
                                appear here
                            </Typography>
                        )}
                    </Paper>
                </Grid>
            </Grid>
            <Grid container spacing={2} mt={2}>
                <Grid size={12}>
                    <Paper elevation={0} style={{ padding: "10px" }}>
                        <Typography variant="h6" fontWeight="bold" mb={1}>
                            Associated CORFQs
                        </Typography>
                        <DataGridPro
                            pinnedColumns={pinnedColumns}
                            onPinnedColumnsChange={handlePinnedColumnsChange}
                            rows={orgProfileDetails?.Associated_Company_RFQ}
                            columns={columnsRfq}
                            getRowHeight={() => "auto"}
                            initialState={{
                                pagination: { paginationModel: { pageSize: 4 } }
                            }}
                            getRowId={(row) => row.rfqID}
                            sx={{
                                width: "100%",
                                "& .MuiDataGrid-columnHeaderTitleContainer": {
                                    "& .MuiDataGrid-sortIcon": {
                                        opacity: "1 !important",
                                        visibility: "visible !important"
                                    }
                                },
                                "& .MuiDataGrid-cell": {
                                    display: "flex",
                                    alignItems: "center"
                                },
                                "& .MuiDataGrid-scrollbarFiller--pinnedRight": {
                                    backgroundColor: "#F4F4F4!important"
                                }
                            }}
                        />
                    </Paper>
                </Grid>
            </Grid>
        </>
    ) : (
        <Typography variant="h6" textAlign="center" mt={2}>
            No data found
        </Typography>
    );
};
